import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { CaptchaStep, MfaCodeForm, MfaSetupForm, BackupCodes } from "../components/AuthChallenge";

// Deliberately bland: no product branding, no hint of what this console is.
export default function PlatformLogin() {
  const { login, verifyMfa, setupMfa, enableMfa, commitAuth } = useAuth();
  const [stage, setStage] = useState("form"); // form | mfa | mfa_setup | backup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaId, setCaptchaId] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [setupToken, setSetupToken] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [authData, setAuthData] = useState(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Sign in";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => { document.title = prevTitle; document.head.removeChild(meta); };
  }, []);

  const resetCaptcha = () => { setCaptchaAnswer(""); setCaptchaKey((k) => k + 1); };

  const submit = (e) => {
    e.preventDefault();
    setError("");
    setShowCaptcha(true);
  };

  const runAuth = async () => {
    setError(""); setLoading(true);
    try {
      const res = await login(email, password, { id: captchaId, answer: captchaAnswer });
      if (res.done) return;
      setShowCaptcha(false);
      if (res.mfa_required) { setMfaToken(res.mfa_token); setStage("mfa"); }
      else if (res.mfa_setup_required) {
        const s = await setupMfa(res.setup_token);
        setSetupToken(res.setup_token); setSetupData(s); setStage("mfa_setup");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const isCaptcha = detail && typeof detail === "object" && String(detail.code || "").includes("captcha");
      setError(formatApiError(detail) || "Unable to sign in");
      resetCaptcha();
      if (!isCaptcha) setShowCaptcha(false);
    } finally { setLoading(false); }
  };

  const doMfa = async (code) => {
    setError(""); setLoading(true);
    try { await verifyMfa(mfaToken, code); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Invalid code"); }
    finally { setLoading(false); }
  };

  const doSetup = async (code) => {
    setError(""); setLoading(true);
    try { const data = await enableMfa(setupToken, code); setAuthData(data); setStage("backup"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Invalid code"); }
    finally { setLoading(false); }
  };

  const shell = (children) => (
    <div style={{ minHeight: "100dvh", background: "#0B0D14", display: "grid", placeItems: "center", padding: 24, paddingTop: "calc(env(safe-area-inset-top) + 24px)" }}>
      <div style={{ width: "100%", maxWidth: 340 }}>{children}</div>
    </div>
  );

  if (stage === "mfa") return shell(<MfaCodeForm onSubmit={doMfa} error={error} loading={loading} />);
  if (stage === "mfa_setup") return shell(<MfaSetupForm setup={setupData} onSubmit={doSetup} error={error} loading={loading} forced />);
  if (stage === "backup") return shell(<BackupCodes codes={authData?.backup_codes || []} onDone={() => commitAuth(authData)} />);
  if (showCaptcha) return shell(
    <CaptchaStep
      onVerify={runAuth}
      onBack={() => { setShowCaptcha(false); setError(""); }}
      resetKey={captchaKey} answer={captchaAnswer} setAnswer={setCaptchaAnswer} onId={setCaptchaId}
      error={error} loading={loading} submitLabel="Verify & sign in"
    />
  );

  return shell(
    <form onSubmit={submit} data-testid="platform-login-form" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <input data-testid="platform-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="Email" required autoComplete="username"
        style={{ width: "100%", borderRadius: 8, padding: "12px 14px", fontSize: 16, outline: "none",
          background: "#12151F", border: "1px solid #1E2233", color: "#E7E4DB" }} />
      <input data-testid="platform-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
        placeholder="Password" required autoComplete="current-password"
        style={{ width: "100%", borderRadius: 8, padding: "12px 14px", fontSize: 16, outline: "none",
          background: "#12151F", border: "1px solid #1E2233", color: "#E7E4DB" }} />
      {error && <p data-testid="platform-login-error" style={{ fontSize: 12, color: "#E0685E", margin: 0 }}>{error}</p>}
      <button data-testid="platform-login-submit" type="submit" disabled={loading}
        style={{ width: "100%", borderRadius: 8, padding: "12px", fontSize: 14, fontWeight: 600,
          background: "#7C83FF", color: "#fff", border: "none", opacity: loading ? 0.6 : 1 }}>
        {loading ? "…" : "Sign in"}
      </button>
    </form>
  );
}
